import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Checkbox, FormControlLabel, Typography, Alert, Stack, Chip,
} from '@mui/material';
import { useMutation, useQuery } from 'react-query';
import { format, parseISO } from 'date-fns';
import api, { deaLogsApi, type AdministrationLogRecord } from '../../utils/api';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  log: AdministrationLogRecord;
  /** Firebase uid of the signed-in user — advisory self-witness check only; the backend enforces via INV-25. */
  currentUserId: string;
}

const SOURCE_LABEL: Record<string, string> = {
  medication: 'medication charge-out',
  vaccination: 'vaccination',
  rxDispenseEvent: 'Rx dispense',
};

/**
 * Vet+witness sign-off for a pending auto-logged controlled-substance entry
 * (ADR-0014). The record is immutable once signed; quantity/lot corrections —
 * before or after signing — go through the DEA amendment flow (no-erasures),
 * which currently exists backend-only for auto rows. Online-only — a network
 * failure surfaces as an error here, DEA writes never queue offline.
 */
const AttestAdministrationDialog: React.FC<Props> = ({ open, onClose, onSuccess, log, currentUserId }) => {
  const [witnessId, setWitnessId] = useState('');
  const [witnessAttests, setWitnessAttests] = useState(false);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const witnessesQuery = useQuery(
    ['vet-witnesses'],
    () => api.get('/users/veterinarians').then((res) => res.data),
    { enabled: open },
  );

  const mutation = useMutation(
    () =>
      deaLogsApi.attestAdministration(log.id, {
        witnessId: witnessId || undefined,
        witnessAttests: witnessId ? witnessAttests : undefined,
        notes: notes || undefined,
      }),
    {
      onSuccess: () => {
        onSuccess();
        handleClose();
      },
      onError: (err: any) => {
        setError(err?.response?.data?.message ?? 'Failed to attest administration');
      },
    },
  );

  const dispenserId = log.administeredBy?.id;
  const witnessIsDispenser = !!witnessId && witnessId === dispenserId;
  const witnessIsSelf = !!witnessId && witnessId === currentUserId;
  // Witness-required substances keep the hard rule. An authority-pending row
  // (requiresWitness === false — unsigned only because the dispenser lacked
  // administer authority) can be signed witness-less by a veterinarian ("vet
  // approval or witness"); the backend enforces the attester's authority and
  // its 400 surfaces in the error alert. Missing/legacy field → required.
  const witnessRequired = log.substance?.requiresWitness !== false;
  const canSubmit =
    !mutation.isLoading &&
    !witnessIsDispenser &&
    !witnessIsSelf &&
    (witnessId ? witnessAttests : !witnessRequired);

  const handleClose = () => {
    setWitnessId('');
    setWitnessAttests(false);
    setNotes('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Attest Controlled-Substance Entry</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            Auto-logged from a {SOURCE_LABEL[log.sourceType ?? ''] ?? 'dispense'}
            {log.administeredAt ? ` on ${format(parseISO(log.administeredAt), 'yyyy-MM-dd HH:mm')}` : ''}.
            Attesting signs the record and it becomes immutable — verify the quantity and lot are
            correct first. Corrections require a formal DEA amendment (no erasures).
          </Alert>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" fontWeight={600}>
              {log.substance?.genericName ?? '—'}
            </Typography>
            {log.substance?.strength && (
              <Typography variant="caption" color="text.secondary">{log.substance.strength}</Typography>
            )}
            <Chip
              size="small"
              label={`${log.quantityAdministered ?? '—'} ${log.substance?.unit ?? ''} · lot ${log.lot?.lotNumber ?? '—'}`}
            />
            {log.patient?.name && <Chip size="small" variant="outlined" label={log.patient.name} />}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Dispensed by {log.administeredBy?.name ?? 'unknown'}
          </Typography>

          <TextField
            select fullWidth size="small"
            label={witnessRequired ? 'Witness (required)' : 'Witness (optional for veterinarians)'}
            value={witnessId}
            onChange={(e) => setWitnessId(e.target.value)}
            disabled={witnessesQuery.isLoading}
            error={witnessIsDispenser || witnessIsSelf}
            helperText={
              witnessIsDispenser
                ? 'The dispensing staff member cannot witness their own administration'
                : witnessIsSelf
                  ? 'Witness must be a different user'
                  : witnessRequired
                    ? ''
                    : 'Needs veterinarian approval: attest as a vet without a witness, or add one'
            }
          >
            {(witnessesQuery.data ?? []).map((u: any) => (
              <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
            ))}
          </TextField>

          <FormControlLabel
            control={<Checkbox checked={witnessAttests} onChange={(e) => setWitnessAttests(e.target.checked)} />}
            label={
              <Typography variant="body2">
                Witness attests to this administration and any waste
              </Typography>
            }
          />

          <TextField
            fullWidth multiline minRows={2} label="Notes (optional)" size="small"
            value={notes} onChange={(e) => setNotes(e.target.value)}
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={() => mutation.mutate()}>
          {mutation.isLoading ? 'Signing…' : 'Attest & Sign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttestAdministrationDialog;
